type PlanValue = string | string[] | React.ReactNode;

type DisplayType = 'bullet' | 'text';

type PlanDetail = {
  name: PlanValue;
  value: PlanValue;
  type?: DisplayType;
};

type InsurancePlan = {
  name: string;
  annualBenefitLimit: PlanDetail;
  benefitPercentage: PlanDetail;
  excess: PlanDetail;
  eligibility: PlanDetail;
  coverage: PlanDetail;
  consultation: PlanDetail;
  paralysis: PlanDetail;
  crucialLigament: PlanDetail;
  hipJoint: PlanDetail;
  emergencyBoarding: PlanDetail;
  waitingPeriod: PlanDetail;
  dentalIllness: PlanDetail;
  optionalExtras: PlanDetail;
};

type InsurancePlans = {
  [key: string]: InsurancePlan;
};

const DOG_INSURANCE_PLAN_SILVER: InsurancePlan = {
  name: 'Silver',
  annualBenefitLimit: {
    name: 'Annual Benefit Limit',
    value: 'Up to $20,000',
  },
  benefitPercentage: {
    name: 'Benefit Percentage',
    value: '80%',
  },
  excess: {
    name: 'Excess',
    value: ['$0', '$250', '$500'],
  },
  eligibility: {
    name: 'Eligibility',
    value: 'Older than 8 weeks and younger than 15 years',
  },
  coverage: {
    name: 'Coverage',
    value: (
      <span>
        Specified Accidental Injuries and Specified Illnesses only<sup>*</sup>
      </span>
    ),
  },
  consultation: {
    name: (
      <span>
        Consultation sub-limit (Home visits included)<sup>1</sup>
      </span>
    ),
    value: '$500',
  },
  paralysis: {
    name: 'Paralysis Tick sub-limit',
    value: '$2,000',
  },
  crucialLigament: {
    name: 'Crucial Ligament Conditions sub-limit',
    value: '$3,000 per leg per policy period',
  },
  hipJoint: {
    name: 'Hip Joint Surgery sub-limit',
    value: '$6,000 per hip, per policy period (Total Hip Replacements are included)',
  },
  emergencyBoarding: {
    name: 'Emergency Boarding sub-limit',
    value: '$1,000',
  },
  waitingPeriod: {
    name: 'Waiting Periods',
    value: [
      '0 days for Specified Accidental Injuries',
      '21 days for Specified Illnesses',
      '6 months for Cruciate Ligament Conditions and Optional Dental Illness Benefit, if selected',
    ],
    type: 'bullet',
  },
  dentalIllness: {
    name: 'Dental Illness Benefit',
    value: '$1,000 (Optional)',
  },
  optionalExtras: {
    name: 'Optional Extras Available',
    value: 'Routine Care (non-insurance benefit)',
  },
};

const {
  annualBenefitLimit,
  benefitPercentage,
  excess,
  eligibility,
  coverage,
  consultation,
  paralysis,
  crucialLigament,
  hipJoint,
  emergencyBoarding,
  waitingPeriod,
  dentalIllness,
  optionalExtras,
} = DOG_INSURANCE_PLAN_SILVER;

const DOG_INSURANCE_PLAN_GOLD: InsurancePlan = {
  ...DOG_INSURANCE_PLAN_SILVER,
  name: 'Gold',
  annualBenefitLimit,
  benefitPercentage,
  excess,
  eligibility: {
    ...eligibility,
    value: 'Older than 8 weeks and younger than 9 years',
  },
  coverage: {
    ...coverage,
    value: (
      <span>
        Specified Accidental Injuries and Illnesses<sup>†</sup>
      </span>
    ),
  },
  consultation,
  paralysis: {
    ...paralysis,
    value: '$3,000',
  },
  crucialLigament: {
    ...crucialLigament,
    value: '$4,000 per leg per policy period',
  },
  hipJoint: {
    ...hipJoint,
    value: '$6,000 per hip, per policy period (Total Hip Replacements are included)',
  },
  emergencyBoarding,
  waitingPeriod: {
    ...waitingPeriod,
    value: [
      '0 days for Specified Accidental Injuries',
      '21 days for Illnesses',
      '6 months for Cruciate Ligament Conditions and Optional Dental Illness Benefit, if selected',
    ],
  },
  dentalIllness,
  optionalExtras,
};

const UTB = 'Up to annual benefit limit';

const DOG_INSURANCE_PLAN_PLATINUM: InsurancePlan = {
  ...DOG_INSURANCE_PLAN_GOLD,
  name: 'Platinum',
  annualBenefitLimit: {
    ...annualBenefitLimit,
    value: 'Up to $30,000',
  },
  benefitPercentage,
  excess,
  eligibility: DOG_INSURANCE_PLAN_GOLD.eligibility,
  coverage: DOG_INSURANCE_PLAN_GOLD.coverage,
  consultation: {
    ...consultation,
    value: UTB,
  },
  paralysis: {
    ...paralysis,
    value: UTB,
  },
  crucialLigament: {
    ...crucialLigament,
    value: UTB,
  },
  hipJoint: {
    ...hipJoint,
    value: UTB,
  },
  emergencyBoarding: {
    ...emergencyBoarding,
    value: '$1,500',
  },
  waitingPeriod: {
    ...waitingPeriod,
    value: [
      '0 days for Specified Accidental Injuries',
      '21 days for Illnesses',
      '6 months for Crucial Ligament Conditions and Dental Illness Benefit',
    ],
  },
  dentalIllness: {
    ...dentalIllness,
    value: '$1,000 (included)',
  },
  optionalExtras: {
    ...optionalExtras,
    value: 'Routine Care (non-insurance benefit)',
  },
};

const DOG_HEADERS = [DOG_INSURANCE_PLAN_SILVER.name, DOG_INSURANCE_PLAN_GOLD.name, DOG_INSURANCE_PLAN_PLATINUM.name];

const DOG_INSURANCE_PLANS: InsurancePlans = {
  [DOG_INSURANCE_PLAN_SILVER.name]: DOG_INSURANCE_PLAN_SILVER,
  [DOG_INSURANCE_PLAN_GOLD.name]: DOG_INSURANCE_PLAN_GOLD,
  [DOG_INSURANCE_PLAN_PLATINUM.name]: DOG_INSURANCE_PLAN_PLATINUM,
};

const CAT_INSURANCE_PLAN_BRONZE: InsurancePlan = {
  ...DOG_INSURANCE_PLAN_SILVER,
  name: 'Bronze',
  eligibility: {
    ...eligibility,
    value: 'For cats older than 8 weeks and younger than 15 years',
  },
  paralysis: {
    ...paralysis,
    value: 'n/a',
  },
  crucialLigament: {
    name: 'Cruciate Ligament Conditions sub limit',
    value: 'n/a',
  },
  hipJoint: {
    ...hipJoint,
    value: 'n/a',
  },
  waitingPeriod: {
    ...waitingPeriod,
    value: [
      '21 days for Specified Conditions (except ingestion of a foreign object and poisoning - 0 days)',
      '6 months for Optional Dental Illness Benefit, if selected',
    ],
  },
};

const CAT_INSURANCE_PLAN_GOLD: InsurancePlan = {
  ...CAT_INSURANCE_PLAN_BRONZE,
  name: 'Gold',
  eligibility: {
    ...eligibility,
    value: 'Older than 8 weeks and younger than 9 years',
  },
  waitingPeriod: {
    ...waitingPeriod,
    value: [
      '0 days for Specified Accidental Injuries',
      '21 days for Illnesses',
      '6 months for Cruciate Ligament Conditions and Optional Dental Illness Benefit, if selected',
    ],
  },
  paralysis: {
    ...paralysis,
    value: '$3,000',
  },
  crucialLigament: {
    ...CAT_INSURANCE_PLAN_BRONZE.crucialLigament,
    value: '$4,000 per leg per policy period',
  },
  hipJoint: {
    ...hipJoint,
    value: '$6,000 per hip, per policy period (Total Hip Replacements are included)',
  },
  coverage: {
    ...coverage,
    value: DOG_INSURANCE_PLAN_GOLD.coverage.value,
  },
};

const CAT_INSURANCE_PLAN_PLATINUM: InsurancePlan = {
  ...CAT_INSURANCE_PLAN_BRONZE,
  name: 'Platinum',
  annualBenefitLimit: {
    ...annualBenefitLimit,
    value: '$30,000',
  },
  eligibility: {
    ...CAT_INSURANCE_PLAN_GOLD.eligibility,
  },
  waitingPeriod: {
    ...waitingPeriod,
    value: [
      '0 days for Specified Accidental Injuries',
      '21 days for Illnesses',
      '6 months for Cruciate Ligament Conditions and Dental Illness Benefit',
    ],
  },
  consultation: {
    ...consultation,
    value: UTB,
  },
  paralysis: {
    ...paralysis,
    value: UTB,
  },
  crucialLigament: {
    ...CAT_INSURANCE_PLAN_BRONZE.crucialLigament,
    value: UTB,
  },
  hipJoint: {
    ...hipJoint,
    value: UTB,
  },
  emergencyBoarding: {
    ...emergencyBoarding,
    value: '$1,500',
  },
  dentalIllness: {
    ...dentalIllness,
    value: '$1,000 (included)',
  },
  coverage: {
    ...coverage,
    value: CAT_INSURANCE_PLAN_GOLD.coverage.value,
  },
};

const CAT_HEADERS = [CAT_INSURANCE_PLAN_BRONZE.name, CAT_INSURANCE_PLAN_GOLD.name, CAT_INSURANCE_PLAN_PLATINUM.name];

const CAT_INSURANCE_PLANS: InsurancePlans = {
  [CAT_INSURANCE_PLAN_BRONZE.name]: CAT_INSURANCE_PLAN_BRONZE,
  [CAT_INSURANCE_PLAN_GOLD.name]: CAT_INSURANCE_PLAN_GOLD,
  [CAT_INSURANCE_PLAN_PLATINUM.name]: CAT_INSURANCE_PLAN_PLATINUM,
};

export { DOG_INSURANCE_PLANS, CAT_INSURANCE_PLANS, DOG_HEADERS, CAT_HEADERS };
export type { PlanDetail, InsurancePlan, InsurancePlans, PlanValue, DisplayType };
